import { generateTicks, scanTicks, type Band, type TickInformation } from './core';
import {
  convertImpliedProbabilityToAmerican,
  convertAmericanToDecimal,
} from '@/core/utils';

// DO NOT UPDATE WITHOUT ALSO PROPERLY UPDATING AND COORDINATING DEPLOYMENT WITH THE BACKEND
const positiveBands: Band[] = [
  [100, 110, 0.5],
  [110, 200, 0.5],
  [200, 300, 1],
  [300, 400, 1],
  [400, 500, 1],
  [500, 1000, 1],
  // abs >= 1000 handled by getAmericanTickInfoAbove1000
];

function generateAmericanTicks(): number[] {
  const positive = generateTicks(positiveBands, 1);
  const negative = positive.filter((t) => t !== 100).map((t) => -t);
  return [...negative, ...positive].sort((a, b) => a - b);
}

const ticks = generateAmericanTicks();

function getAmericanTickInfoAbove1000(value: number): TickInformation {
  const step = 1;
  // Number-line ordering, matching scanTicks and getDecimalTickInfoAbove10:
  // next is the next valid tick greater than value, prev the next tick less.
  // A magnitude-based form reverses prev/next for negative odds (e.g. -1217 ->
  // next: -1218), which made the mirrored-tick default offer land one tick
  // below the top offer on longshot markets.
  const floor = Math.floor(value / step) * step;
  if (floor === value) {
    return {
      prev: value - step,
      snap: value,
      next: value + step,
    };
  }
  const ceil = floor + step;
  const snap = value - floor < ceil - value ? floor : ceil;
  return {
    prev: floor,
    snap,
    next: ceil,
  };
}

function preprocessAmerican(raw: number): number {
  if (raw > 0 && raw < 1) return convertImpliedProbabilityToAmerican(raw * 100);
  if (raw >= -100 && raw < 100) return 100;
  return raw;
}

export function getAmericanTickInfo(rawValue: number): TickInformation {
  const value = preprocessAmerican(rawValue);
  if (Math.abs(value) >= 1000) return getAmericanTickInfoAbove1000(value);
  return scanTicks(value, ticks);
}

